"use client";

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useEffect,
  useState,
  ReactNode
} from "react";
import { loginErp, logoutErp } from "@/core/api/erp-auth";
import { getAuthenticatedCurrentErpUser } from "@/core/api/erp-personal-profile";
import { buildAuthUser, mergeAuthUserProfile } from "@/core/auth/profile";
import {
  clearStoredAuthUser,
  readStoredAuthUser,
  SESSION_CHANGED_EVENT,
  writeStoredAuthUser
} from "@/core/auth/session";
import type { AuthUser, Role } from "@/core/auth/session";
import type { ErpEmployeeAccountDto } from "@/contracts";
import { unregisterErpNotificationDevice } from "@/core/api/erp-notifications";
import { readNotificationInstallationId } from "@/core/notifications/installation";

export type { AuthUser, Role };

const SEED_USERS: AuthUser[] = [
  {
    username: "admin",
    password: "admin",
    fullName: "Nguyễn Văn Admin",
    role: "ADMIN",
    roleLabel: "Quản trị viên",
    email: "admin@company.vn"
  }
];

const USERS_KEY = "erp.users";
export const SHOULD_BYPASS_LOGIN = false;

async function refreshCurrentUserProfile(currentUser: AuthUser) {
  if (!currentUser.accessToken) {
    throw new Error("Phiên đăng nhập không hợp lệ");
  }

  const profile = await getAuthenticatedCurrentErpUser();
  const latestSession = readStoredAuthUser() || currentUser;
  const nextUser = mergeAuthUserProfile(latestSession, profile);
  writeStoredAuthUser(nextUser);
  return nextUser;
}

function loadUsers(): AuthUser[] {
  if (typeof window === "undefined") return SEED_USERS;
  try {
    const raw = localStorage.getItem(USERS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  localStorage.setItem(USERS_KEY, JSON.stringify(SEED_USERS));
  return SEED_USERS;
}

interface AuthContextValue {
  user: AuthUser | null;
  users: AuthUser[];
  hydrated: boolean;
  login: (
    email: string,
    password: string
  ) => Promise<{ ok: boolean; message?: string; user?: AuthUser }>;
  logout: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  mergeCurrentUserProfile: (profile: ErpEmployeeAccountDto) => AuthUser | null;
  createUser: (u: Omit<AuthUser, never>) => { ok: boolean; message?: string };
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [users, setUsers] = useState<AuthUser[]>(SEED_USERS);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useLayoutEffect(() => {
    let cancelled = false;

    async function hydrateSession() {
      try {
        const storedUser = readStoredAuthUser();
        if (storedUser) {
          const refreshedUser = await refreshCurrentUserProfile(storedUser);
          if (!cancelled) setUser(refreshedUser);
        } else if (SHOULD_BYPASS_LOGIN) {
          const defaultUser = SEED_USERS[0];
          writeStoredAuthUser(defaultUser);
          if (!cancelled) setUser(defaultUser);
        } else if (!cancelled) {
          setUser(null);
        }
      } catch {
        clearStoredAuthUser();
        if (SHOULD_BYPASS_LOGIN) {
          const defaultUser = SEED_USERS[0];
          writeStoredAuthUser(defaultUser);
          if (!cancelled) setUser(defaultUser);
        } else if (!cancelled) {
          setUser(null);
        }
      } finally {
        if (!cancelled) setHydrated(true);
      }
    }

    void hydrateSession();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncSession = () => {
      setUser(readStoredAuthUser());
    };
    const syncStorageSession = (event: StorageEvent) => {
      if (event.key === "erp.session") syncSession();
    };

    window.addEventListener(SESSION_CHANGED_EVENT, syncSession);
    window.addEventListener("storage", syncStorageSession);
    return () => {
      window.removeEventListener(SESSION_CHANGED_EVENT, syncSession);
      window.removeEventListener("storage", syncStorageSession);
    };
  }, []);

  useLayoutEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    setUsers(loadUsers());
    /* eslint-enable react-hooks/set-state-in-effect */
  }, []);

  useEffect(() => {
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
  }, [users]);

  const login = async (email: string, password: string) => {
    try {
      const authSession = await loginErp({ email, password });
      const u = buildAuthUser(email, authSession);

      setUser(u);
      writeStoredAuthUser(u);
      return { ok: true, user: u };
    } catch (error) {
      console.error(error);
      return {
        ok: false,
        message: error instanceof Error ? error.message : "Lỗi kết nối máy chủ"
      };
    }
  };

  const logout = async () => {
    const token = user?.accessToken;
    const installationId = readNotificationInstallationId();
    if (token && installationId) {
      try {
        await unregisterErpNotificationDevice(installationId);
      } catch {
        // Continue logout even when the device unregister request fails.
      }
    }
    setUser(null);
    clearStoredAuthUser();
    await logoutErp(token);
  };

  const refreshUserProfile = async () => {
    if (!user) return;
    const refreshedUser = await refreshCurrentUserProfile(user);
    setUser(refreshedUser);
  };

  const mergeCurrentUserProfile = useCallback(
    (profile: ErpEmployeeAccountDto) => {
      if (!user) return null;
      const nextUser = mergeAuthUserProfile(user, profile);
      setUser(nextUser);
      writeStoredAuthUser(nextUser);
      return nextUser;
    },
    [user]
  );

  const createUser = (u: AuthUser) => {
    if (
      users.some((x) => x.username.toLowerCase() === u.username.toLowerCase())
    ) {
      return { ok: false, message: "Mã nhân viên đã tồn tại" };
    }
    setUsers((s) => [...s, u]);
    return { ok: true };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        users,
        hydrated,
        login,
        logout,
        refreshUserProfile,
        mergeCurrentUserProfile,
        createUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Which nav groups each role can access
export const ROLE_GROUPS: Record<Role, string[]> = {
  ADMIN: ["B1", "HR", "PROJECTS"],
  HR: ["HR", "PROJECTS"],
  PM: ["HR", "PROJECTS"],
  ACCOUNTANT: ["HR", "PROJECTS"],
  ENGINEER: ["HR", "PROJECTS"],
  VIEWER: ["HR", "PROJECTS"]
};

export const ROLE_LABELS: Record<Role, string> = {
  ADMIN: "Quản trị viên",
  HR: "Nhân sự",
  PM: "Quản lý dự án",
  ACCOUNTANT: "Kế toán",
  ENGINEER: "Kỹ sư công trường",
  VIEWER: "Người xem báo cáo"
};
